import { Component } from '@theme/component';

const API_URL = 'https://zlwuitnvqotqodtnhrmd.supabase.co/functions/v1/edu-hub-ask';
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inpsd3VpdG52cW90cW9kdG5ocm1kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU2NDgzMDUsImV4cCI6MjA5MTIyNDMwNX0.aywJfPCbNjK9mSErr7wGbSgdADHmCYufzEnoI0Y5IoQ';
const MIN_CHARS = 10;
const MAX_CHARS = 500;
const COOLDOWN_MS = 8000;

/**
 * @typedef {Object} ItokRefs
 * @property {HTMLTextAreaElement} input
 * @property {HTMLButtonElement} submitBtn
 * @property {HTMLElement} responseArea
 * @property {HTMLElement} inputArea
 * @property {HTMLElement} questionDisplay
 * @property {HTMLElement} answerText
 * @property {HTMLElement} articlesSection
 * @property {HTMLElement} articlesList
 * @property {HTMLElement} questionsSection
 * @property {HTMLElement} questionsGrid
 * @property {HTMLElement} noAnswerSection
 * @property {HTMLElement} skeleton
 * @property {HTMLElement} disclaimer
 * @property {HTMLElement} cancelBtn
 * @property {HTMLInputElement} honeypot
 * @property {HTMLElement} emailForm
 * @property {HTMLElement} inputHint
 * @property {HTMLElement} debugPanel
 * @property {HTMLElement} debugEntries
 * @property {HTMLButtonElement} updateFilesBtn
 * @property {HTMLButtonElement} clearCacheBtn
 * @property {HTMLElement} adminStatus
 */

/**
 * Is This OK? — AI-powered skincare Q&A component
 * @extends {Component<ItokRefs>}
 */
class IsThisOk extends Component {
  #lastSubmitTime = 0;
  #abortController = null;
  #sessionId = '';
  #loadingTextInterval = null;
  #debugEnabled = false;
  #requestStartTime = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#sessionId = `itok_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.#debugEnabled = this.dataset.debug === 'true';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelRequest();
    this.#stopLoadingTextCycle();
  }

  // ============================================================
  // EVENT HANDLERS
  // ============================================================

  /** @param {Event} _event */
  handleSubmit(_event) {
    const currentState = this.dataset.state;

    // "Ask a Follow Up" resets to idle
    if (['answered', 'no_answer', 'off_topic', 'off_topic_question', 'rate_limited', 'error'].includes(currentState)) {
      this.#resetToIdle();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const question = this.refs.input?.value?.trim();
    if (!question) {
      this.#showHint('Please type a question first.');
      return;
    }
    if (question.length < MIN_CHARS) {
      this.#showHint(`Your question is a bit short — try adding more detail (at least ${MIN_CHARS} characters).`);
      return;
    }
    if (question.length > MAX_CHARS) {
      this.#showHint(`That's a bit long — please keep it under ${MAX_CHARS} characters.`);
      return;
    }
    this.#clearHint();
    this.#askQuestion(question);
  }

  handleCancel() {
    this.#cancelRequest();
    this.#resetToIdle();
  }

  /** @param {Event} event */
  handleQuestionChip(event) {
    const path = event.composedPath?.() || [];
    const actualTarget = /** @type {HTMLElement} */ (path[0] || event.target);
    const chip = actualTarget.closest('[data-question]');
    if (!chip) return;
    const question = /** @type {HTMLElement} */ (chip).dataset.question;
    if (!question) return;

    // Reset cooldown so chip clicks aren't throttled
    this.#lastSubmitTime = 0;
    this.#clearHint();
    // Go directly to asking — don't reset to idle first
    this.#askQuestion(question);
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /** @param {KeyboardEvent} event */
  handleInputKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.handleSubmit(event);
    }
  }

  /** @param {PointerEvent} event */
  async handleArticleClick(event) {
    // Component framework proxies event.target to the on:click element (the div),
    // so use composedPath()[0] to get the actual clicked element (the <a>)
    const path = event.composedPath?.() || [];
    const actualTarget = /** @type {HTMLElement} */ (path[0] || event.target);
    const link = actualTarget.closest?.('.itok__article-link');
    if (!link) return;
    event.preventDefault();

    const href = link.getAttribute('href') || '';
    const match = href.match(/\/blogs\/[^/]+\/([^/?]+)/);
    if (!match) {
      window.location.href = href;
      return;
    }

    const handle = match[1];
    const popup = document.querySelector('question-popup-component');

    if (popup && typeof /** @type {any} */ (popup).openByHandle === 'function') {
      const opened = await /** @type {any} */ (popup).openByHandle(handle);
      if (!opened) window.location.href = href;
    } else {
      window.location.href = href;
    }
  }

  // ============================================================
  // CORE
  // ============================================================

  /** @param {string} question */
  async #askQuestion(question) {
    if (question.length < MIN_CHARS || question.length > MAX_CHARS) return;

    const now = Date.now();
    const timeSince = now - this.#lastSubmitTime;
    if (timeSince < COOLDOWN_MS) {
      const secs = Math.ceil((COOLDOWN_MS - timeSince) / 1000);
      this.#showHint(`Please wait ${secs} second${secs === 1 ? '' : 's'} before asking another question.`);
      return;
    }
    this.#lastSubmitTime = now;
    if (this.refs.honeypot?.value) return;

    this.#clearHint();
    this.#setLoading(question);
    this.#abortController = new AbortController();
    this.#requestStartTime = Date.now();

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ANON_KEY}`,
          'x-session-id': this.#sessionId,
        },
        body: JSON.stringify({
          question,
          session_id: this.#sessionId,
          model: this.dataset.model || 'gpt-4.1-mini',
          _hp: this.refs.honeypot?.value || '',
        }),
        signal: this.#abortController.signal,
      });

      const elapsed = Date.now() - this.#requestStartTime;
      const data = await response.json();
      this.#handleResponse(data);
      this.#renderDebug(question, data, elapsed, response.status);
    } catch (err) {
      if (/** @type {Error} */ (err).name === 'AbortError') return;
      const elapsed = Date.now() - this.#requestStartTime;
      console.error('[ITOK] error:', err);
      this.#showMessage('error');
      this.#renderDebug(question, { status: 'error', error: /** @type {Error} */ (err).message }, elapsed, 0);
    }
  }

  /** @param {Object} data */
  #handleResponse(data) {
    const status = data.status || 'error';

    // Frontend safety net: if status is "answered" but no source quotes were
    // returned, the AI hallucinated an answer without grounded data — treat
    // as no_answer regardless of what the API returned.
    const sources = data.sources || [];
    const hasGroundedSources = sources.length > 0 && sources.some(s => s.quote && s.quote.trim().length > 0);

    if (status === 'answered' && data.answer && hasGroundedSources) {
      this.#showAnswer(data);
    } else if (status === 'answered' && data.answer && !hasGroundedSources) {
      this.#showMessage('no_answer');
    } else if (status === 'off_topic' || status === 'off_topic_question') {
      this.#showMessage(status);
    } else if (status === 'rate_limited') {
      this.#showMessage('rate_limited');
    } else if (status === 'no_answer') {
      this.#showMessage('no_answer');
      if (data.related_questions?.length) {
        this.#renderQuestions(data.related_questions);
        setTimeout(() => {
          this.refs.questionsSection.classList.add('itok--visible', 'itok__stagger-in');
        }, 300);
      }
    } else {
      this.#showMessage('error');
    }
  }

  // ============================================================
  // STATE TRANSITIONS
  // ============================================================

  /** @param {string} question */
  #setLoading(question) {
    this.dataset.state = 'loading';
    this.refs.questionDisplay.textContent = question;
    this.#startLoadingTextCycle();
    this.refs.submitBtn.disabled = true;

    // Clear previous
    this.refs.answerText.innerHTML = '';
    this.refs.articlesList.innerHTML = '';
    this.refs.questionsGrid.innerHTML = '';
    this.refs.disclaimer.classList.remove('itok--visible');
    this.refs.articlesSection.classList.remove('itok--visible', 'itok__stagger-in');
    this.refs.questionsSection.classList.remove('itok--visible', 'itok__stagger-in');
    this.refs.answerText.classList.remove('itok__fade-in');
  }

  /** @param {Object} data */
  #showAnswer(data) {
    this.#stopLoadingTextCycle();
    this.dataset.state = 'answered';
    this.refs.submitBtn.textContent = this.refs.submitBtn.dataset.followupText || 'Ask a Follow Up';
    this.refs.submitBtn.disabled = false;

    this.#renderAnswer(data.answer);

    if (data.show_disclaimer) {
      this.refs.disclaimer.classList.add('itok--visible');
    }

    let delay = 400;

    if (data.related_articles?.length) {
      this.#renderArticles(data.related_articles);
      setTimeout(() => {
        this.refs.articlesSection.classList.add('itok--visible', 'itok__stagger-in');
      }, delay);
      delay += 150;
    }

    if (data.related_questions?.length) {
      this.#renderQuestions(data.related_questions);
      setTimeout(() => {
        this.refs.questionsSection.classList.add('itok--visible', 'itok__stagger-in');
      }, delay);
    }
  }

  /** @param {string} state */
  #showMessage(state) {
    this.#stopLoadingTextCycle();
    this.dataset.state = state;
    this.refs.submitBtn.textContent = this.refs.submitBtn.dataset.followupText || 'Ask a Follow Up';
    this.refs.submitBtn.disabled = false;
  }

  #resetToIdle() {
    this.#stopLoadingTextCycle();
    this.dataset.state = 'idle';
    this.refs.submitBtn.textContent = this.refs.submitBtn.dataset.defaultText || 'Ask Dr. Aegean';
    this.refs.submitBtn.disabled = false;
    this.refs.articlesSection.classList.remove('itok--visible', 'itok__stagger-in');
    this.refs.questionsSection.classList.remove('itok--visible', 'itok__stagger-in');
    this.refs.disclaimer.classList.remove('itok--visible');
    this.refs.answerText.classList.remove('itok__fade-in');
    if (this.refs.input) {
      this.refs.input.value = '';
      this.refs.input.focus();
    }
  }

  // ============================================================
  // RENDER HELPERS
  // ============================================================

  /** @param {string} answer */
  #renderAnswer(answer) {
    const paragraphs = answer.split('\n').filter(Boolean);
    let html = '';

    for (const p of paragraphs) {
      const formatted = p
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="itok__product-link">$1</a>')
        .replace(/^- (.+)$/gm, '<li>$1</li>');

      if (formatted.startsWith('<li>')) {
        html += `<ul>${formatted}</ul>`;
      } else {
        html += `<p>${formatted}</p>`;
      }
    }

    this.refs.answerText.innerHTML = html;
    this.refs.answerText.classList.add('itok__fade-in');
  }

  /** @param {{ title: string; url: string; handle: string }[]} articles */
  #renderArticles(articles) {
    this.refs.articlesList.innerHTML = '';
    for (const article of articles) {
      const link = document.createElement('a');
      link.href = article.url || `/blogs/is-this-ok/${article.handle}`;
      link.className = 'itok__article-link';
      link.textContent = article.title;
      this.refs.articlesList.appendChild(link);
    }
  }

  /** @param {string[]} questions */
  #renderQuestions(questions) {
    this.refs.questionsGrid.innerHTML = '';
    for (const q of questions) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'itok__question-chip';
      chip.dataset.question = q;
      chip.textContent = q;
      this.refs.questionsGrid.appendChild(chip);
    }
  }

  // ============================================================
  // KLAVIYO EMAIL SIGNUP
  // ============================================================

  /** @param {Event} event */
  handleEmailSubmit(event) {
    const form = this.refs.emailForm;
    if (!form) return;
    const emailInput = form.querySelector('input[type="email"]');
    const ctaBtn = form.querySelector('.itok__email-cta');
    if (!emailInput || !ctaBtn) return;

    const email = /** @type {HTMLInputElement} */ (emailInput).value.trim();
    if (!email || !email.includes('@')) {
      this.#showHint('Please enter a valid email address.');
      return;
    }

    const question = this.refs.questionDisplay?.textContent || '';

    ctaBtn.textContent = 'Sending...';
    /** @type {HTMLButtonElement} */ (ctaBtn).disabled = true;

    fetch('https://zlwuitnvqotqodtnhrmd.supabase.co/functions/v1/edu-hub-klaviyo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, question }),
    })
      .then(r => r.json())
      .then(() => {
        ctaBtn.textContent = 'Signed Up!';
        /** @type {HTMLInputElement} */ (emailInput).value = '';
        /** @type {HTMLInputElement} */ (emailInput).disabled = true;
      })
      .catch(() => {
        ctaBtn.textContent = 'Try Again';
        /** @type {HTMLButtonElement} */ (ctaBtn).disabled = false;
      });
  }

  // ============================================================
  // INPUT HINTS
  // ============================================================

  /** @param {string} msg */
  #showHint(msg) {
    if (!this.refs.inputHint) return;
    this.refs.inputHint.textContent = msg;
  }

  #clearHint() {
    if (!this.refs.inputHint) return;
    this.refs.inputHint.textContent = '';
  }

  // ============================================================
  // ADMIN: Update Training Files + Clear Cache (editor only)
  // ============================================================

  async handleUpdateFiles() {
    const btn = this.refs.updateFilesBtn;
    const status = this.refs.adminStatus;
    if (!btn || !status) return;

    const adminKey = this.dataset.adminKey;
    if (!adminKey) {
      status.textContent = 'Admin key is required. Add it in block settings under Developer.';
      status.style.color = 'red';
      return;
    }

    const endpoint = this.dataset.adminEndpoint;
    if (!endpoint) return;

    // Collect file URLs from data attributes
    const files = {};
    const fileMap = {
      'file-system-prompt': 'system_prompt',
      'file-pregnancy-answers': 'pregnancy_answers',
      'file-articles': 'articles',
      'file-writing': 'writing',
      'file-instagram': 'instagram',
      'file-newsletters': 'newsletters',
      'file-copy-bible': 'copy_bible',
      'file-custom-qa': 'custom_qa',
    };

    for (const [attr, key] of Object.entries(fileMap)) {
      const url = this.dataset[attr.replace(/-([a-z])/g, (_, c) => c.toUpperCase())];
      if (url) files[key] = url;
    }

    if (!Object.keys(files).length) {
      status.textContent = 'No file URLs configured. Add URLs in block settings under Training Files.';
      status.style.color = 'red';
      return;
    }

    btn.dataset.loading = 'true';
    status.textContent = 'Fetching files and uploading to AI...';
    status.style.color = '';

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey}`,
        },
        body: JSON.stringify({ files }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        status.textContent = `Training files updated. ${data.files_uploaded || 0} file(s) replaced in AI knowledge base.`;
        status.style.color = 'green';
      } else {
        status.textContent = `Error: ${data.error || 'Unknown error'}`;
        status.style.color = 'red';
      }
    } catch (err) {
      status.textContent = `Network error: ${/** @type {Error} */ (err).message}`;
      status.style.color = 'red';
    } finally {
      btn.dataset.loading = 'false';
    }
  }

  async handleClearCache() {
    const btn = this.refs.clearCacheBtn;
    const status = this.refs.adminStatus;
    if (!btn || !status) return;

    const adminKey = this.dataset.adminKey;
    if (!adminKey) {
      status.textContent = 'Admin key is required. Add it in block settings under Developer.';
      status.style.color = 'red';
      return;
    }

    const endpoint = this.dataset.cacheEndpoint;
    if (!endpoint) return;

    btn.dataset.loading = 'true';
    status.textContent = 'Clearing answer cache...';
    status.style.color = '';

    try {
      const res = await fetch(`${endpoint}?action=flush_cache`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.success) {
        status.textContent = 'Answer cache cleared. New questions will generate fresh answers.';
        status.style.color = 'green';
      } else {
        status.textContent = `Error: ${data.error || 'Unknown error'}`;
        status.style.color = 'red';
      }
    } catch (err) {
      status.textContent = `Network error: ${/** @type {Error} */ (err).message}`;
      status.style.color = 'red';
    } finally {
      btn.dataset.loading = 'false';
    }
  }

  // ============================================================
  // THEME EDITOR
  // ============================================================

  /** @param {string} state */
  showState(state) {
    this.refs.questionDisplay.textContent = 'Example question from the theme editor?';

    if (state === 'answered') {
      this.dataset.state = 'answered';
      this.refs.submitBtn.textContent = this.refs.submitBtn.dataset.followupText || 'Ask a Follow Up';
      this.refs.submitBtn.disabled = false;
      this.refs.answerText.innerHTML = '<p><strong>Short answer:</strong> This is a preview of how an answered question looks.</p><p>The actual answer content will appear here when a real question is asked.</p><p><strong>Bottom line:</strong> This is just a preview — your skin is doing great.</p>';
      this.refs.answerText.classList.add('itok__fade-in');
      this.#renderArticles([{ title: 'Example Article Title', url: '#', handle: 'example' }]);
      this.refs.articlesSection.classList.add('itok--visible');
      this.#renderQuestions(['What about vitamin C during pregnancy?', 'Is sunscreen safe while breastfeeding?', 'Do I need to change my routine?']);
      this.refs.questionsSection.classList.add('itok--visible');
    } else if (state === 'no_answer' || state === 'off_topic') {
      this.#showMessage(state);
      this.#renderQuestions(['Is retinol safe during pregnancy?', 'What moisturizer should I use?', 'Can I use salicylic acid?']);
      this.refs.questionsSection.classList.add('itok--visible');
    }
  }

  // ============================================================
  // DEBUG
  // ============================================================

  /**
   * @param {string} question
   * @param {Object} data
   * @param {number} elapsed
   * @param {number} httpStatus
   */
  #renderDebug(question, data, elapsed, httpStatus) {
    if (!this.#debugEnabled || !this.refs.debugEntries) return;

    this.refs.debugEntries.innerHTML = '';
    const debug = data._debug || data.debug || {};
    const entries = [];

    // Question
    entries.push({ label: 'Question', value: question });

    // Pipeline source & classifier from API
    entries.push({ label: 'Source', value: debug.source || 'unknown', highlight: true });
    entries.push({ label: 'Classifier', value: debug.classifier || 'unknown', highlight: true });
    if (debug.reason) entries.push({ label: 'Reason', value: debug.reason });
    if (debug.model) entries.push({ label: 'Model', value: debug.model });

    // Timing in seconds
    const totalSec = debug.total_latency_ms ? (debug.total_latency_ms / 1000).toFixed(2) : (elapsed / 1000).toFixed(2);
    entries.push({ label: 'Response Time', value: `${totalSec}s` });
    if (debug.ai_latency_ms) entries.push({ label: 'AI Latency', value: `${(debug.ai_latency_ms / 1000).toFixed(2)}s` });
    if (debug.input_tokens) entries.push({ label: 'Input Tokens', value: debug.input_tokens.toLocaleString() });
    if (debug.output_tokens) entries.push({ label: 'Output Tokens', value: debug.output_tokens.toLocaleString() });
    const cost = debug.estimated_cost_usd;
    entries.push({ label: 'Est. Cost', value: cost != null ? (cost === 0 ? '$0 (no AI call)' : `$${cost.toFixed(6)}`) : 'unknown' });

    // Sources check — flag frontend override
    const sources = data.sources || [];
    const hasGroundedSources = sources.length > 0 && sources.some(s => s.quote && s.quote.trim().length > 0);
    entries.push({ label: 'Sources', value: `${sources.length} returned (${hasGroundedSources ? 'grounded' : 'empty/missing'})` });
    if (data.status === 'answered' && !hasGroundedSources) {
      entries.push({ label: 'Frontend Override', value: 'answered → no_answer (no grounded sources)', highlight: true });
    }

    // Content flags
    if (data.show_disclaimer != null) entries.push({ label: 'Disclaimer', value: data.show_disclaimer ? 'Shown' : 'Hidden' });
    if (data.related_articles?.length) entries.push({ label: 'Related Articles', value: `${data.related_articles.length} returned` });
    if (data.related_questions?.length) entries.push({ label: 'Related Questions', value: `${data.related_questions.length} returned` });
    if (data.recommended_products?.length) entries.push({ label: 'Products Injected', value: data.recommended_products.map(p => p.title).join(', ') });

    // Render entries
    for (const entry of entries) {
      const div = document.createElement('div');
      div.className = `itok__debug-entry${entry.highlight ? ' itok__debug-entry--header' : ''}`;
      div.innerHTML = `<span class="itok__debug-label">${entry.label}:</span>${this.#escapeHtml(String(entry.value))}`;
      this.refs.debugEntries.appendChild(div);
    }

    // Sources with file + quote
    if (data.sources?.length) {
      const sourcesHeader = document.createElement('div');
      sourcesHeader.className = 'itok__debug-entry itok__debug-entry--header';
      sourcesHeader.innerHTML = `<span class="itok__debug-label">Sources:</span>${data.sources.length} reference(s)`;
      this.refs.debugEntries.appendChild(sourcesHeader);

      for (const source of data.sources) {
        const sourceDiv = document.createElement('div');
        sourceDiv.className = 'itok__debug-entry';
        let html = `<span class="itok__debug-label">File:</span>${this.#escapeHtml(source.file || 'unknown')}`;

        if (source.quote) {
          html += `<div class="itok__debug-source">"${this.#escapeHtml(source.quote)}"</div>`;
        }
        sourceDiv.innerHTML = html;
        this.refs.debugEntries.appendChild(sourceDiv);
      }
    }
  }

  /** @param {string} str */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ============================================================
  // UTILITIES
  // ============================================================

  #startLoadingTextCycle() {
    this.#stopLoadingTextCycle();
    const btn = this.refs.submitBtn;
    const texts = [
      btn.dataset.loadingText,
      btn.dataset.loadingText2,
      btn.dataset.loadingText3,
      btn.dataset.loadingText4,
    ].filter(Boolean);
    if (!texts.length) return;

    let index = 0;
    btn.textContent = texts[0];
    this.#loadingTextInterval = setInterval(() => {
      index = (index + 1) % texts.length;
      btn.textContent = texts[index];
    }, 3000);
  }

  #stopLoadingTextCycle() {
    if (this.#loadingTextInterval) {
      clearInterval(this.#loadingTextInterval);
      this.#loadingTextInterval = null;
    }
  }

  #cancelRequest() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }
}

customElements.define('is-this-ok', IsThisOk);

export default IsThisOk;
